import { setupHomeDesignOptionsPanel } from './home-design-options';
import { setupLazyLoadSVG } from './lazyload-svg';
import { LOADER_SVG } from './helpers/constants';
import { simpleHash } from './helpers/tools';

window.addEventListener("load", () => {
    const floorplanGenerator = document.querySelector("[data-floorplan-generator]");
    if (floorplanGenerator) {
        const homeDesignSelect = floorplanGenerator.querySelector("select[name='homeDesignSelect']");
        if (homeDesignSelect) {
            let firstRun = true;
            const firstSelectedHomeDesignId = homeDesignSelect.value;
            const firstHomeFloorplanPanel = floorplanGenerator.querySelector("section.home-floorplan-option-panel");
            initializeDownloadJpgButton(firstHomeFloorplanPanel, firstSelectedHomeDesignId);
            
            homeDesignSelect.addEventListener("change", () => {
                homeDesignSelect.disabled = true;
                const selectedHomeDesignId = homeDesignSelect.value;
                let homeDesignTitle = document.querySelector(`select[name="homeDesignSelect"] option[value="${selectedHomeDesignId}"]`)?.getAttribute('data-title');
                const oldHomeFloorplanPanel = floorplanGenerator.querySelector("section.home-floorplan-option-panel");

                if (firstRun) {
                    const pleaseSelectH1 = floorplanGenerator.querySelector('h1[data-visible]');
                    if (pleaseSelectH1) {
                        pleaseSelectH1.dataset.visible = 'false';
                        setTimeout(() => {
                            pleaseSelectH1.remove();
                        }, 700);
                    }
                    if (homeDesignSelect.classList.contains('select-glow')) {
                        homeDesignSelect.classList.remove('select-glow', 'animate-select-glow');
                    }
                    firstRun = false;
                }

                if (!selectedHomeDesignId) {
                    console.warn("No home design selected. Clearing panel.");
                    if (oldHomeFloorplanPanel) {
                        // Clear the panel or show a placeholder message
                        oldHomeFloorplanPanel.innerHTML = '<p>Please select a home design to see options.</p>';
                    }
                    return; // Stop further execution
                }

                const apiUrl = `/actions/kingsbridge/api/render-floorplan-generator-panel?home-design-id=${encodeURIComponent(selectedHomeDesignId)}`;

                fetch(apiUrl)
                    .then(response => {
                        if (!response.ok) {
                            // If response is not OK, try to get text for more detailed error
                            return response.text().then(text => {
                                throw new Error(`HTTP error! Status: ${response.status}, Message: ${text}`);
                            });
                        }
                        return response.text(); // Get the response as HTML text
                    })
                    .then(html => {
                        const oldPanelFade = oldHomeFloorplanPanel?.querySelector('section.home-floorplan-option-panel div[data-panel-fade]');
                        if (oldPanelFade) {
                            oldPanelFade.dataset.panelFade = 'false';
                        }

                        // Create a temporary container to parse the HTML string
                        const tempContainer = document.createElement('div');
                        tempContainer.innerHTML = html.trim(); // .trim() to remove potential whitespace issues

                        // The API is expected to return the new panel element itself
                        const newHomeFloorplanPanel = tempContainer.firstChild;

                        if (newHomeFloorplanPanel && newHomeFloorplanPanel.nodeType === Node.ELEMENT_NODE) {
                            floorplanGenerator.appendChild(newHomeFloorplanPanel);
                            setupHomeDesignOptionsPanel(floorplanGenerator);
                            setupLazyLoadSVG(floorplanGenerator);
                            newHomeFloorplanPanel.closest('section')?.classList.remove('!hidden');
                            setTimeout(() => {
                                if (oldHomeFloorplanPanel) {
                                    oldHomeFloorplanPanel.remove();
                                }
                                const panelFade = newHomeFloorplanPanel.querySelector('section.home-floorplan-option-panel div[data-panel-fade]');
                                if (panelFade) {
                                    panelFade.dataset.panelFade = 'true';
                                }
                                homeDesignSelect.disabled = false;
                            }, 700);
                            initializeDownloadJpgButton(newHomeFloorplanPanel, selectedHomeDesignId);
                        } else {
                            console.error("Fetched HTML is not a valid element or is empty.", html);
                            // Optionally, display an error message in the UI
                            const errorMsgElement = document.createElement('p');
                            errorMsgElement.textContent = 'Error: The floorplan options could not be loaded correctly.';
                            errorMsgElement.style.color = 'red';
                            floorplanGenerator.appendChild(errorMsgElement);
                        }
                    })
                    .catch(error => {
                        console.error("Error fetching or processing floorplan panel:", error);
                        // Display an error message in the UI, potentially in the old panel's location or a new element
                        const errorDisplay = oldHomeFloorplanPanel || floorplanGenerator;
                        errorDisplay.innerHTML = `<p style="color: red;">Failed to load floorplan options: ${error.message}</p>`;
                    });
                
                gtmPush('select-home-design', homeDesignTitle);
            });
        }
    }
    
    function initializeDownloadJpgButton(panel, selectedHomeDesignId) {
        const downloadJpgButton = panel.querySelector('[data-download-floorplan-jpg]');
        if (!downloadJpgButton) return;

        const originalButtonContent = downloadJpgButton.innerHTML;

        downloadJpgButton.addEventListener('click', () => {
            downloadJpgButton.disabled = true;
            downloadJpgButton.innerHTML = LOADER_SVG;

            const title = downloadJpgButton.getAttribute('data-title');
            
            let optionIdsParameter = [];
            let optionIds = [];
            let optionTitles = [];
            const options = panel.querySelectorAll("input[data-floorplan-switch]:checked");
            options.forEach((option) => {
                let optionId = option.getAttribute('data-option-id');
                let title = option.getAttribute('data-title');
                optionIdsParameter.push("option-ids[]=" + optionId);
                optionIds.push(optionId);
                optionTitles.push(title);
            });
            let optionIdsString = optionIdsParameter.join("&");

            // Sort the collected optionIds numerically for hash generation
            optionIds.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            
            const svgUrl = `/actions/kingsbridge/pdf/floorplan-jpeg?home-design-id=${encodeURIComponent(selectedHomeDesignId)}&${optionIdsString}`;

            fetch(svgUrl)
                .then(response => response.text())
                .then(svgText => {
                    const img = new Image();
                    const canvas = document.createElement('canvas'); // Create canvas dynamically
                    const downloadLink = document.createElement('a'); // Create download link dynamically

                    img.onload = function () {
                        const padding = 32;
                        const targetWidth = 1000;
                        const aspectRatio = img.height / img.width;
                        const targetHeight = targetWidth * aspectRatio;

                        canvas.width = targetWidth + (padding * 2);
                        canvas.height = targetHeight + (padding * 2);

                        const ctx = canvas.getContext('2d');

                        // Fill the entire canvas (including padding area) with white
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);

                        // Draw the image with padding
                        ctx.drawImage(img, padding, padding, targetWidth, targetHeight);

                        const jpegData = canvas.toDataURL('image/jpeg', 0.92);

                        let filename = title;
                        // If there are any selected options, generate and append the hash
                        if (optionIds.length > 0) {
                            const dashedOptionIds = optionIds.join('-'); // Join sorted IDs with a dash
                            const optionsHash = simpleHash(dashedOptionIds).toUpperCase();
                            filename += ` - ${optionsHash}`;
                        }
                        filename += '.jpeg';

                        downloadLink.href = jpegData;
                        downloadLink.download = filename;
                        document.body.appendChild(downloadLink); // Temporarily add to body to click
                        downloadLink.click();
                        document.body.removeChild(downloadLink); // Clean up
                    };

                    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText);
                })
                .catch(error => {
                    console.error('Error loading SVG:', error);
                })
                .finally(() => {
                    downloadJpgButton.disabled = false;
                    downloadJpgButton.innerHTML = originalButtonContent;
                });

            let homeDesignTitle = document.querySelector(`select[name="homeDesignSelect"] option[value="${selectedHomeDesignId}"]`)?.getAttribute('data-title');
            gtmPush('download-home-design', homeDesignTitle, optionTitles);
        });
    }

    function gtmPush(event, homeDesign, options = []) {
        if (typeof dataLayer !== 'undefined') {
            let params = {
                event: event,
                homeDesign: homeDesign
            };
            
            if (event === 'download-home-design') {
                if (options.length === 0) {
                    options = "No options selected.";
                }
                
                params['options'] = options; 
            }
            
            dataLayer.push(params);
        }
    }
});